import { Link } from "react-router-dom";

import {
  PLATFORM_NAMES,
  WEEKDAYS,
  eventsOn,
  monthGrid,
  sameDay,
  timeOf,
  type CalendarEvent,
} from "../lib/calendar";

/** A month, Monday first. Each day with meetings is marked in its platforms'
 * colours; hovering a day lists them, clicking picks it. `full` (the Calendar
 * page) writes the meetings into the days; otherwise (the dashboard) they're
 * coloured marks and the chosen day is listed underneath. */
export function MonthGrid({ events, month, selected, onSelect, full = false }: {
  events: CalendarEvent[];
  month: Date;
  selected: Date | null;
  onSelect: (day: Date) => void;
  full?: boolean;
}) {
  const today = new Date();
  return (
    <div className={`month${full ? " full" : ""}`} role="grid" aria-label={monthName(month)}>
      {WEEKDAYS.map((name) => (
        <span key={name} className="month-weekday" role="columnheader">
          {full ? name : name[0]}
        </span>
      ))}
      {monthGrid(month).map((day) => {
        const dayEvents = eventsOn(events, day);
        const classes = ["month-day"];
        if (day.getMonth() !== month.getMonth()) classes.push("other");
        if (sameDay(day, today)) classes.push("today");
        if (selected && sameDay(day, selected)) classes.push("selected");
        if (dayEvents.length) classes.push("busy", `p-${dayEvents[0].platform ?? "none"}`);
        return (
          <button
            key={day.toISOString()}
            className={classes.join(" ")}
            role="gridcell"
            onClick={() => onSelect(day)}
            title={dayEvents.length ? dayEvents.map((e) => `${timeOf(e.start)} ${e.title}`).join("\n") : undefined}
            aria-label={`${day.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}${dayEvents.length ? `, ${dayEvents.length} meeting${dayEvents.length === 1 ? "" : "s"}` : ""}`}
          >
            <span className="month-date">{day.getDate()}</span>
            {full && (
              <span className="month-events">
                {dayEvents.slice(0, 3).map((event) => (
                  <span key={event.key} className={`chip p-${event.platform ?? "none"}`}>
                    <span className="chip-time">{timeOf(event.start)}</span> {event.title}
                  </span>
                ))}
                {dayEvents.length > 3 && <span className="chip-more">+{dayEvents.length - 3} more</span>}
              </span>
            )}
            {/* Dots: the small month, and the full one on a phone, where the
                titles don't fit. */}
            {dayEvents.length > 0 && (
              <span className="month-marks" aria-hidden>
                {dayEvents.slice(0, 3).map((event) => (
                  <span key={event.key} className={`mark p-${event.platform ?? "none"}`} />
                ))}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** The meetings on one day: when, what, where, and the way in. */
export function DayEvents({ day, events, empty = "No meetings this day." }: {
  day: Date;
  events: CalendarEvent[];
  empty?: string;
}) {
  const list = eventsOn(events, day);
  return (
    <div className="day-events">
      <p className="day-events-head">
        {day.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
      </p>
      {list.length === 0 ? (
        <p className="day-events-empty">{empty}</p>
      ) : (
        <ul>
          {list.map((event) => (
            <EventRow key={event.key} event={event} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function EventRow({ event, withDay = false }: { event: CalendarEvent; withDay?: boolean }) {
  return (
    <li className={`event-row p-${event.platform ?? "none"}`}>
      <span className="event-time">
        {withDay && <span className="event-day">{dayName(event.start)}</span>}
        {timeOf(event.start)}–{timeOf(event.end)}
      </span>
      <span className="event-main">
        {event.sessionId ? (
          <Link className="event-title" to={`/meetings/${event.sessionId}`}>{event.title}</Link>
        ) : (
          <span className="event-title">{event.title}</span>
        )}
        <span className="event-meta">
          {event.platform && <span className="event-platform">{PLATFORM_NAMES[event.platform]}</span>}
          <span>{event.upcoming ? (event.state === "cancelled" ? "Not recording" : "Bot joins") : event.label}</span>
          {event.upcoming && event.url && (
            <a href={event.url} target="_blank" rel="noreferrer">Meeting link ↗</a>
          )}
        </span>
      </span>
    </li>
  );
}

/** The colour key for the three platforms. */
export function PlatformLegend() {
  return (
    <p className="platform-legend">
      {(Object.keys(PLATFORM_NAMES) as (keyof typeof PLATFORM_NAMES)[]).map((key) => (
        <span key={key}>
          <span className={`mark p-${key}`} aria-hidden />
          {PLATFORM_NAMES[key]}
        </span>
      ))}
    </p>
  );
}

/** Today, Tomorrow, or "Thu 2 Oct". */
function dayName(date: Date): string {
  const today = new Date();
  if (sameDay(date, today)) return "Today";
  if (sameDay(date, new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1))) return "Tomorrow";
  return date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export function monthName(month: Date): string {
  return month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
